<script>
  import { createEventDispatcher } from 'svelte';

  import { formatEther, parseEther } from 'viem';
  import { getPublicClient, sendTransaction } from "@wagmi/core";

  import { chainId, chainSymbol, chainExplorer, walletAddress } from '../stores.js';
  import { getContractType, getContractValue, getContractJSONvalue, updateLogs } from '../utils.js';
  import { contractEvents } from '../db.js';

  /** @type {number} */
  export let chain;
  /** @type {string} */
  export let address;
  /** @type {number} */
  export let amount = 0;
  export let loading;

  /** @type {boolean} */
  let show = true;
  /** @type {boolean} */
  let opened;
  /** @type {bigint} */
  let allowance;
  /** @type {bigint} */
  let price;
  /** @type {number} */
  let maxAmount = 0;
  let soldEvent;
  /** @type {boolean} */
  let soldOut;
  /** @type {number} */
  let userAmount = amount;
  /** @type {boolean} */
  let selling;
  /** @type {string} */
  let txHash;
  /** @type {string} */
  let feedback;
  let toEther = false;

  $: $walletAddress, init();
  $: $soldEvent, checkSoldOut();

  const init = async () => {
    if ($walletAddress) {
      opened = getContractValue(chain, address, 'opened');
      if (open) {
        const [ contractAddress, holder, tokenId, pric, am ] = await Promise.all([
          getContractValue(chain, address, 'contractAddress'),
          getContractValue(chain, address, 'holder'),
          getContractValue(chain, address, 'tokenId'),
          getContractValue(chain, address, 'price'),
          getContractValue(chain, address, 'amount')
        ]);
        price = pric;
        maxAmount = am;
        if (await getContractType(contractAddress, chain) === 'Collection2') {
          if (await getContractJSONvalue(chain, contractAddress, tokenId, 'decimals')) {
            var connected = await getContractValue(chain, contractAddress, 'isApprovedForAll', [ holder, address ]);
            toEther = true;
          } else {
            amount = 1;
            const mintRole = await getContractValue(chain, contractAddress, 'MINTER_ROLE');
            connected = await getContractValue(chain, contractAddress, 'hasRole', [ mintRole, address ]);
          }
          if (connected) {
            await updateLogs(chain, address, loading);
            soldEvent = contractEvents(chain, address, 'Sold');
          } else {
            show = false;
          }
        } else {
          toEther = true;
          allowance = await getContractValue(chain, contractAddress, 'allowance', [holder, address]);
          if (allowance >= (price * BigInt(amount || 1))) {
            await updateLogs(chain, address, loading);
            soldEvent = contractEvents(chain, address, 'Sold');
          } else {
            show = false;
          }
        }
      } else {
        show = false;
      }
    }
  }

  const checkSoldOut = () => {
    if ($soldEvent) {
      const total = $soldEvent.map(e => e.args.amount).reduce((a, b) => a + b, 0)
      soldOut = total === maxAmount;
    }
  }

  const buy = async () => {
    selling = true;
    try {
      const value = toEther ? parseEther((userAmount || 1).toString()) : BigInt(userAmount || 1) * price;
      const { hash } = await sendTransaction({ to: address, value });
      txHash = hash;
    } catch (err) {
      if (err.shortMessage === 'User rejected the request.') {
        dispatch('user-cancel');
        new CustomEvent('user-cancel');
        selling = undefined;
      } else {
        console.log(err)
        dispatch('error', err);
        new CustomEvent('error', { detail: err });
      }
      return;
    }

    dispatch('hashed', txHash);
    new CustomEvent('hashed', { detail: txHash });
    selling = false;
    const publicClient = getPublicClient({ chainId: $chainId });
    const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash });
    if (receipt.status === 'success') {
      dispatch('sold', receipt)
      new CustomEvent('sold', { detail: receipt });
      feedback = 'Done!';
      txHash = undefined;
      userAmount = undefined;
      setTimeout(() => { feedback = undefined }, 5000);
    }
  }

  const dispatch = createEventDispatcher();
</script>

{#if show}
  <span class="lt-vending-btn">
    {#if soldOut === undefined}
      <span class="loading">Loading...</span>
    {:else if soldOut}
      <span class="soldOut">Sold out</span>
    {:else if feedback}
      <span class="feedback">{ feedback }</span>
    {:else if txHash}
      <a href="{ $chainExplorer.url }/tx/{ txHash }" target="_blank" class="txHash">View tx</a>
    {:else if price && amount}
      <button on:click={ buy }>
        {#if selling}
          <span class="loading loading-spinner"></span>
        {/if}
        <span class="price">
          { formatEther(price) }</span><span class="amount">{ $chainSymbol }
        </span>
      </button>
    {:else if price && !amount}
      <div class="group">
        <input type="number" min="0" placeholder="amount" bind:value={ userAmount } />
        <button on:click={ buy }>
          {#if selling}
            <span class="loading loading-spinner"></span>
          {/if}
          <span class="multiplier">x</span>
          <span class="price">{ formatEther(price) }</span>
          <span class="symbol">{ $chainSymbol }</span>
        </button>
      </div>
    {/if}
  </span>
{/if}